import { useState, useEffect, useCallback } from 'react';

const CYCLE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

export type CyclePhase = 'refinement' | 'doing' | 'wrapup';

interface UseCycleTimerReturn {
  isCycleActive: boolean;
  cycleTimeRemaining: number;
  currentPhase: CyclePhase | null;
  startCycle: () => void;
  cancelCycle: () => void;
}

export function useCycleTimer(): UseCycleTimerReturn {
  const [isCycleActive, setIsCycleActive] = useState(false);
  const [cycleTimeRemaining, setCycleTimeRemaining] = useState(0);
  const [currentPhase, setCurrentPhase] = useState<CyclePhase | null>(null);

  const startCycle = useCallback(() => {
    setIsCycleActive(true);
    setCycleTimeRemaining(CYCLE_DURATION);
    setCurrentPhase('refinement');
  }, []);

  const cancelCycle = useCallback(() => {
    setIsCycleActive(false);
    setCycleTimeRemaining(0);
    setCurrentPhase(null);
  }, []);

  useEffect(() => {
    if (!isCycleActive) return;

    const interval = setInterval(() => {
      setCycleTimeRemaining((prev) => {
        const newTime = prev - 1000;
        
        if (newTime <= 0) {
          setIsCycleActive(false);
          setCurrentPhase(null);
          return 0;
        }

        // Determine phase based on remaining time
        const minutesRemaining = Math.floor(newTime / 1000 / 60);
        if (minutesRemaining >= 25) {
          setCurrentPhase('refinement');
        } else if (minutesRemaining >= 5) {
          setCurrentPhase('doing');
        } else {
          setCurrentPhase('wrapup');
        }

        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isCycleActive]);

  return {
    isCycleActive,
    cycleTimeRemaining,
    currentPhase,
    startCycle,
    cancelCycle,
  };
}
